/**
 * Drives a build turn's prototype version on the backend versions API
 * (PROTOTYPER_STABILITY_TECH_PLAN.md P6.4a):
 *
 *   turn start / resume ─▶ begin()    create or return the turn's draft, hold a 30 s build
 *                                     lease renewed every 10 s
 *   verified build      ─▶ verified() version verified, prototype pointer moves
 *   failed build        ─▶ failed()   code failure (counts toward automatic fixes) or
 *                                     environment failure (never does)
 *   turn aborted        ─▶ stop()     stop renewing the lease
 *   fix limit reached   ─▶ current()  read the version's fix counts for the card (P6.4b)
 *
 * Every call degrades to null instead of throwing: while the versions API is unavailable
 * (the SQL script not applied, a network error) builds keep working exactly as before.
 */

export interface TrackedVersion {
  id: string;
  status: string;
  /** The number the backend gave the version when it was verified (V1, V2 ... per prototype). */
  number?: number | null;
  fixAttempts?: number;
  fixAttemptsGranted?: number;
}

export interface VersionFailResult {
  status: string;
  fixAllowed: boolean;
  fixAttempts: number;
  fixAttemptsGranted: number;
}

/** A recorded failure and the version it belongs to (the card's actions need the id). */
export interface TrackedVersionFailure extends VersionFailResult {
  versionId: string;
}

export interface VersionTurnContext {
  projectId: string;
  conversationId: string;
  llmInteractionId: string;
}

/** The subset of the core prototyper service the tracker uses. */
export interface VersionApi {
  createPrototypeVersion(
    projectId: string,
    conversationId: string,
    body: { llmInteractionId: string; chefProjectId?: string; prototypeDisplayName?: string }
  ): Promise<TrackedVersion>;
  leasePrototypeVersion(
    projectId: string,
    conversationId: string,
    versionId: string,
    owner: string
  ): Promise<{ acquired: boolean }>;
  verifyPrototypeVersion(
    projectId: string,
    conversationId: string,
    versionId: string,
    body: { contentHash?: string; snapshotId?: string; chefProjectId?: string }
  ): Promise<TrackedVersion>;
  failPrototypeVersion(
    projectId: string,
    conversationId: string,
    versionId: string,
    body: {
      kind: 'code' | 'environment';
      errorSignature?: string;
      buildLog?: string;
      reason?: 'timeout';
    }
  ): Promise<VersionFailResult>;
}

export const LEASE_RENEW_MS = 10_000;
/** Matches the backend `FailVersionDto` caps. */
const MAX_BUILD_LOG_CHARS = 64_000;
const MAX_SIGNATURE_CHARS = 2_000;

interface Timers {
  setInterval: (fn: () => void, ms: number) => unknown;
  clearInterval: (handle: unknown) => void;
}

const browserTimers: Timers = {
  setInterval: (fn, ms) => globalThis.setInterval(fn, ms),
  clearInterval: (handle) => globalThis.clearInterval(handle as ReturnType<typeof setInterval>),
};

export function createVersionTracker(deps: {
  api: VersionApi;
  /** Lease owner: stable per tab (see `getWorkbenchTabId`). */
  owner: string;
  timers?: Timers;
  warn?: (message: string, detail: unknown) => void;
  /** A version this tab verified or failed; other tabs are told (P6.4e). */
  onVersionChange?: (ctx: VersionTurnContext, status: string) => void;
}) {
  const timers = deps.timers ?? browserTimers;
  const warn = deps.warn ?? ((message, detail) => console.warn(message, detail));
  /** Draft (or its in-flight creation) per turn. */
  const versionsByTurn = new Map<string, Promise<TrackedVersion | null>>();
  let heartbeat: { turn: string; handle: unknown } | null = null;

  const stop = (): void => {
    if (heartbeat) {
      timers.clearInterval(heartbeat.handle);
      heartbeat = null;
    }
  };

  const versionFor = (
    ctx: VersionTurnContext,
    chefProjectId?: string | null
  ): Promise<TrackedVersion | null> => {
    const existing = versionsByTurn.get(ctx.llmInteractionId);
    if (existing) return existing;
    const created = deps.api
      .createPrototypeVersion(ctx.projectId, ctx.conversationId, {
        llmInteractionId: ctx.llmInteractionId,
        ...(chefProjectId ? { chefProjectId } : {}),
      })
      .catch((err: unknown) => {
        warn('[Workbench] version draft unavailable', err);
        // Forget the failure so a later call for this turn can try again.
        versionsByTurn.delete(ctx.llmInteractionId);
        return null;
      });
    versionsByTurn.set(ctx.llmInteractionId, created);
    return created;
  };

  const lease = (ctx: VersionTurnContext, versionId: string): void => {
    void deps.api
      .leasePrototypeVersion(ctx.projectId, ctx.conversationId, versionId, deps.owner)
      .catch((err: unknown) => warn('[Workbench] version lease failed', err));
  };

  return {
    /** Create or return the turn's draft and hold its build lease until verified/failed/stop. */
    async begin(
      ctx: VersionTurnContext,
      chefProjectId?: string | null
    ): Promise<TrackedVersion | null> {
      const version = await versionFor(ctx, chefProjectId);
      if (!version) return null;
      if (heartbeat?.turn !== ctx.llmInteractionId) {
        stop();
        lease(ctx, version.id);
        heartbeat = {
          turn: ctx.llmInteractionId,
          handle: timers.setInterval(() => lease(ctx, version.id), LEASE_RENEW_MS),
        };
      }
      return version;
    },

    /**
     * Create or return the turn's draft and take its build lease before building (P6.4e).
     * `held-elsewhere`: another tab or device is building this turn, so do not start.
     * `unavailable`: the versions API did not answer; build as before.
     */
    async acquire(
      ctx: VersionTurnContext,
      chefProjectId?: string | null
    ): Promise<'acquired' | 'held-elsewhere' | 'unavailable'> {
      const version = await versionFor(ctx, chefProjectId);
      if (!version) return 'unavailable';
      let acquired: boolean;
      try {
        ({ acquired } = await deps.api.leasePrototypeVersion(
          ctx.projectId,
          ctx.conversationId,
          version.id,
          deps.owner
        ));
      } catch (err) {
        warn('[Workbench] version lease failed', err);
        return 'unavailable';
      }
      if (!acquired) {
        if (heartbeat?.turn === ctx.llmInteractionId) stop();
        return 'held-elsewhere';
      }
      if (heartbeat?.turn !== ctx.llmInteractionId) {
        stop();
        heartbeat = {
          turn: ctx.llmInteractionId,
          handle: timers.setInterval(() => lease(ctx, version.id), LEASE_RENEW_MS),
        };
      }
      return 'acquired';
    },

    async verified(
      ctx: VersionTurnContext,
      details: { contentHash?: string; snapshotId?: string; chefProjectId?: string | null }
    ): Promise<TrackedVersion | null> {
      const version = await versionFor(ctx, details.chefProjectId);
      if (heartbeat?.turn === ctx.llmInteractionId) stop();
      if (!version) return null;
      try {
        const next = await deps.api.verifyPrototypeVersion(
          ctx.projectId,
          ctx.conversationId,
          version.id,
          {
            ...(details.contentHash ? { contentHash: details.contentHash } : {}),
            ...(details.snapshotId ? { snapshotId: details.snapshotId } : {}),
            ...(details.chefProjectId ? { chefProjectId: details.chefProjectId } : {}),
          }
        );
        versionsByTurn.set(ctx.llmInteractionId, Promise.resolve(next));
        deps.onVersionChange?.(ctx, next.status);
        return next;
      } catch (err) {
        warn('[Workbench] version verify failed', err);
        return null;
      }
    },

    async failed(
      ctx: VersionTurnContext,
      failure: {
        kind: 'code' | 'environment';
        errorSignature?: string | null;
        buildLog?: string | null;
        /** `timeout`: the turn ran out of its 25-minute budget. */
        reason?: 'timeout';
      }
    ): Promise<TrackedVersionFailure | null> {
      const version = await versionFor(ctx);
      if (heartbeat?.turn === ctx.llmInteractionId) stop();
      if (!version) return null;
      try {
        const result = await deps.api.failPrototypeVersion(
          ctx.projectId,
          ctx.conversationId,
          version.id,
          {
            kind: failure.kind,
            ...(failure.reason ? { reason: failure.reason } : {}),
            ...(failure.errorSignature
              ? { errorSignature: failure.errorSignature.slice(0, MAX_SIGNATURE_CHARS) }
              : {}),
            ...(failure.buildLog ? { buildLog: failure.buildLog.slice(-MAX_BUILD_LOG_CHARS) } : {}),
          }
        );
        deps.onVersionChange?.(ctx, result.status);
        return { ...result, versionId: version.id };
      } catch (err) {
        warn('[Workbench] version fail report failed', err);
        return null;
      }
    },

    /** The turn's version as the server has it now (fix counts), bypassing the cached draft. */
    async current(ctx: VersionTurnContext): Promise<TrackedVersion | null> {
      try {
        const version = await deps.api.createPrototypeVersion(ctx.projectId, ctx.conversationId, {
          llmInteractionId: ctx.llmInteractionId,
        });
        versionsByTurn.set(ctx.llmInteractionId, Promise.resolve(version));
        return version;
      } catch (err) {
        warn('[Workbench] version read failed', err);
        return null;
      }
    },

    /** Stop renewing the lease (turn aborted or superseded); it expires on the server. */
    stop,
  };
}

export type VersionTracker = ReturnType<typeof createVersionTracker>;

const TAB_ID_KEY = 'ideoz:workbench:tab-id:v1';

/** Stable id for this tab (sessionStorage), matching the backend lease owner format. */
export function getWorkbenchTabId(): string {
  const make = (): string =>
    `tab-${globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`}`;
  try {
    const existing = globalThis.sessionStorage?.getItem(TAB_ID_KEY);
    if (existing && /^[A-Za-z0-9._:-]{8,128}$/.test(existing)) return existing;
    const id = make();
    globalThis.sessionStorage?.setItem(TAB_ID_KEY, id);
    return id;
  } catch {
    return make();
  }
}

/** The last completed `build` tool step's output, for the version's build log. */
export function lastBuildStepOutput(
  steps: ReadonlyArray<{ toolName: string; isComplete?: boolean; result?: unknown }>
): string | null {
  for (let i = steps.length - 1; i >= 0; i--) {
    const step = steps[i]!;
    if (step.toolName === 'build' && step.isComplete) {
      return typeof step.result === 'string' ? step.result : null;
    }
  }
  return null;
}
